import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArrowRight, ShieldCheck, Utensils, Home, Sparkles, MapPin, Leaf, Gift } from "lucide-react";
import Seo from "@/components/Seo";
import FestiveCountdown from "@/components/FestiveCountdown";

const HERO = "https://images.pexels.com/photos/14853538/pexels-photo-14853538.jpeg";
const IMG_ELEGANT = "https://images.pexels.com/photos/27138849/pexels-photo-27138849.jpeg";
const IMG_PLATED = "https://images.unsplash.com/photo-1590741664176-7fbd7e2592a0";
const IMG_CHEF = "https://images.pexels.com/photos/8477058/pexels-photo-8477058.jpeg";
const IMG_TART = "https://images.pexels.com/photos/24247771/pexels-photo-24247771.jpeg";
const IMG_DIWALI = "https://images.pexels.com/photos/8818657/pexels-photo-8818657.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function Landing() {
  const { setAuthOpen, user } = useAuth();

  return (
    <div>
      <Seo
        title="Chef Hire — Private Chefs in Mumbai from ₹75,000/month | HNI Homes"
        description="Hire background-verified private chefs in Mumbai, Bengaluru & Indore from ₹75,000/month — healthy cooking, curated meals & innovative food. Mini-catering & festive gourmet sit-downs. One-time fee of one month's salary + GST, staff on our payroll."
        path="/"
        keywords="chef hire Mumbai, private chef Mumbai, hire a chef Mumbai, home chef Mumbai, mini catering Mumbai, HNI private chef India"
      />
      {/* MACARON GIFT BOX — top highlight */}
      <section className="relative border-b border-[#F5B041]/20 bg-[#120D08]" data-testid="macaron-strip">
        <div className="max-w-7xl mx-auto px-6 py-10 md:py-14 grid md:grid-cols-[320px_1fr] gap-10 items-center">
          <Link to="/diwali" className="block group overflow-hidden border border-[#F5B041]/25">
            <img src="/motichoor-macarons.jpg" alt="Motichoor Macarons — special-edition Diwali gift box"
              data-testid="macaron-strip-image"
              className="w-full aspect-[16/10] md:aspect-[4/3] object-cover transition duration-700 group-hover:scale-[1.04]" />
          </Link>
          <div>
            <span className="chip text-[#F5B041] border-[#F5B041]/40"><Gift size={12} /> Diwali special · Limited edition</span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] mt-5">
              Motichoor Macarons,<br />
              <span className="font-script text-[#F5B041] text-5xl sm:text-6xl">a gift for your loved ones.</span>
            </h2>
            <p className="mt-5 text-neutral-300 max-w-xl leading-relaxed">
              The motichoor laddoo meets the French macaron — saffron, pistachio and edible 24k gold,
              six to an embossed keepsake box. Hand-piped to order for family, clients and festive hampers.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-5">
              <div className="border border-white/10 bg-black/40 px-5 py-3">
                <div className="small-caps text-[10px] text-neutral-500">Box of 6</div>
                <div className="font-serif-display text-3xl text-[#F5B041]">₹1,200</div>
              </div>
              <FestiveCountdown />
              <Link to="/diwali#enquire" data-testid="macaron-strip-cta"
                className="btn-primary small-caps text-[11px] inline-flex items-center gap-2">
                Order gift boxes <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/70 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32 md:pt-32 md:pb-40">
          <div className="flex items-center gap-3 mb-8">
            <span className="chip"><MapPin size={12} /> Mumbai · Delhi NCR · Bengaluru · Hyderabad · Indore</span>
            <span className="chip">HNI · Discreet · Curated</span>
          </div>

          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl tracking-tight">
            The private chef,<br/>
            <span className="font-script text-[#F5B041] text-6xl sm:text-7xl lg:text-8xl">at your table.</span>
          </h1>
          <p className="mt-8 text-lg text-neutral-300 max-w-xl leading-relaxed">
            A curated roster of Indian and international chefs for full-time home postings, and a repertoire of mini-catering menus for the evenings that matter.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="private-chef-highlights">
            <span className="chip text-[#F5B041] border-[#F5B041]/40">Private chefs from ₹75,000/mo</span>
            <span className="chip"><Leaf size={11} /> Healthy cooking</span>
            <span className="chip">Curated meals</span>
            <span className="chip">Innovative food</span>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link to="/chefs" className="btn-primary small-caps text-[11px] inline-flex items-center gap-2" data-testid="cta-browse-chefs">
              Browse Chefs <ArrowRight size={14} />
            </Link>
            <Link to="/menus" className="btn-ghost small-caps text-[11px] inline-flex items-center gap-2" data-testid="cta-browse-menus">
              Explore Mini Catering
            </Link>
            {!user && (
              <button onClick={() => setAuthOpen(true)} className="small-caps text-[11px] text-neutral-400 hover:text-white ml-2" data-testid="cta-signin">
                Sign in →
              </button>
            )}
          </div>

          {/* pricing pill */}
          <div className="mt-16 inline-flex flex-wrap items-center gap-6 border border-white/10 bg-black/50 backdrop-blur px-6 py-4">
            <div>
              <div className="small-caps text-[10px] text-neutral-500">Private chefs</div>
              <div className="font-serif-menu text-xl text-[#F5B041]">From ₹75,000 / mo</div>
            </div>
            <div className="h-8 w-px bg-white/10" />
            <div>
              <div className="small-caps text-[10px] text-neutral-500">Our fee</div>
              <div className="font-serif-menu text-xl">1 salary + GST</div>
            </div>
            <div className="h-8 w-px bg-white/10" />
            <div>
              <div className="small-caps text-[10px] text-neutral-500">India Roster</div>
              <div className="font-serif-menu text-xl">₹50k – ₹1.25L / month</div>
            </div>
            <div className="h-8 w-px bg-white/10" />
            <div>
              <div className="small-caps text-[10px] text-neutral-500">International</div>
              <div className="font-serif-menu text-xl">₹90k – ₹3.55L / month</div>
            </div>
          </div>
        </div>
      </section>

      {/* DIWALI FEATURE BANNER */}
      <section className="relative overflow-hidden grain border-y border-[#F5B041]/15" data-testid="diwali-section">
        <div className="absolute inset-0">
          <img src={IMG_DIWALI} alt="Diwali celebration gourmet dinner" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <span className="chip text-[#F5B041] border-[#F5B041]/40"><Sparkles size={12} /> Festival of Lights · Limited evenings</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] max-w-3xl tracking-tight mt-6">
            Fusional Feast,<br />
            <span className="font-script text-[#F5B041] text-5xl sm:text-6xl lg:text-7xl">a Diwali sit-down.</span>
          </h2>
          <p className="mt-6 text-lg text-neutral-300 max-w-xl leading-relaxed">
            A curated, multi-course festive menu plated at your table by our chefs — the warmth of Diwali,
            composed with restaurant finesse.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <div className="border border-white/10 bg-black/50 backdrop-blur px-6 py-4">
              <div className="small-caps text-[10px] text-neutral-500">Gourmet sit-down</div>
              <div className="font-serif-display text-3xl text-[#F5B041]">from ₹1899 <span className="text-sm text-neutral-400">/ person</span></div>
            </div>
            <Link to="/diwali" data-testid="diwali-banner" className="btn-primary small-caps text-[11px] inline-flex items-center gap-2">
              Reserve your evening <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Two services split */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="max-w-2xl">
          <div className="small-caps text-neutral-500 mb-4">Two ways to dine at home</div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
            A chef in residence,<br/>or a night to remember.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-14">
          <div className="card-lux relative overflow-hidden">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={IMG_CHEF} alt="Private Chef" className="w-full h-full object-cover" />
            </div>
            <div className="p-8">
              <div className="flex items-center gap-2 small-caps text-[10px] text-[#F5B041]">
                <Home size={12} /> Full-time posting
              </div>
              <h3 className="font-serif-menu text-3xl mt-3">Private Chefs</h3>
              <p className="text-neutral-400 mt-3 leading-relaxed">
                Handpicked chefs — Indian and international — who move into your kitchen and cook, curate,
                and manage your household menus month after month.
              </p>
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="chip text-[#F5B041] border-[#F5B041]/40">From ₹75,000/mo</span>
                <span className="chip">Healthy cooking</span>
                <span className="chip">Curated meals</span>
                <span className="chip">Innovative food</span>
              </div>
              <div className="dotted-sep my-6" />
              <div className="flex items-center gap-6 mb-6">
                <div><div className="small-caps text-[10px] text-neutral-500">Cities</div><div className="text-sm">Mumbai · Delhi NCR · Bengaluru · Hyderabad · Indore</div></div>
                <div><div className="small-caps text-[10px] text-neutral-500">Others</div><div className="text-sm">On request</div></div>
              </div>
              <Link to="/chefs" className="btn-primary small-caps text-[11px] inline-flex items-center gap-2" data-testid="service-chefs-cta">
                Meet the chefs <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="card-lux relative overflow-hidden">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={IMG_ELEGANT} alt="Mini Catering" className="w-full h-full object-cover" />
            </div>
            <div className="p-8">
              <div className="flex items-center gap-2 small-caps text-[10px] text-[#F5B041]">
                <Utensils size={12} /> One-evening service
              </div>
              <h3 className="font-serif-menu text-3xl mt-3">Mini Catering</h3>
              <p className="text-neutral-400 mt-3 leading-relaxed">
                Curated menus designed by leading local and international chefs. Open brief — tell us what
                you want and we compose the evening around it.
              </p>
              <div className="dotted-sep my-6" />
              <div className="flex items-center gap-6 mb-6">
                <div><div className="small-caps text-[10px] text-neutral-500">Menus</div><div className="text-sm">Curated · Signature</div></div>
                <div><div className="small-caps text-[10px] text-neutral-500">Brief</div><div className="text-sm">Open, described by you</div></div>
              </div>
              <Link to="/menus" className="btn-primary small-caps text-[11px] inline-flex items-center gap-2" data-testid="service-menus-cta">
                Browse menus <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-white/10 bg-black/30">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-28">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <div className="small-caps text-neutral-500 mb-4">Process</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
                Discreet from<br/>brief to plate.
              </h2>
              <p className="text-neutral-400 mt-6 max-w-md leading-relaxed">
                Every interaction is confidential. NDA-backed staff, verified backgrounds, and a single point of contact.
              </p>
            </div>
            <div className="stagger-in space-y-8">
              {[
                { n: "01", t: "Tell us what you need", d: "City, cuisine, dates, dietary tone. A minute is enough." },
                { n: "02", t: "We match & confirm", d: "We shortlist chefs (or menus) that fit — you meet, you decide." },
                { n: "03", t: "We handle the rest", d: "Onboarding, background checks, payroll, replacement guarantees." },
              ].map((s) => (
                <div key={s.n} className="flex gap-6">
                  <div className="font-serif-display text-4xl text-[#C04000] w-16">{s.n}</div>
                  <div>
                    <div className="font-serif-menu text-2xl">{s.t}</div>
                    <p className="text-neutral-400 mt-1">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feature bento */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: ShieldCheck, t: "Background verified", d: "Every chef is background-checked and NDA-bound before entering your home." },
            { icon: Sparkles, t: "Curated by our team", d: "Menus and chefs shortlisted by a small, senior food editorial team — never scraped." },
            { icon: Utensils, t: "One salary. That's our fee.", d: "A one-time placement fee equal to one month's salary plus GST — no percentage commission. Staff stay on Chef Hire's payroll and report to us." },
          ].map((f) => (
            <div key={f.t} className="card-lux p-8">
              <f.icon size={22} className="text-[#F5B041]" />
              <div className="font-serif-menu text-2xl mt-4">{f.t}</div>
              <p className="text-neutral-400 mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Cities we serve */}
      <section className="max-w-7xl mx-auto px-6 pb-4" data-testid="cities-strip">
        <div className="small-caps text-neutral-500 mb-6 text-center">Cities we serve</div>
        <div className="flex flex-wrap items-stretch justify-center gap-3">
          {[
            { to: "/private-chef-mumbai", c: "Mumbai" },
            { to: "/private-chef-bengaluru", c: "Bengaluru" },
            { to: "/private-chef-indore", c: "Indore" },
            { to: "/private-chef-delhi-ncr", c: "Delhi NCR" },
            { to: "/private-chef-hyderabad", c: "Hyderabad" },
          ].map((x) => (
            <Link
              key={x.to}
              to={x.to}
              data-testid={`city-link-${x.c.toLowerCase().replace(/ /g, "-")}`}
              className="group flex items-center gap-2 border border-white/10 hover:border-[#F5B041]/60 bg-[#141414] px-5 py-3 transition"
            >
              <MapPin size={14} className="text-[#F5B041]" />
              <span className="font-serif-menu text-lg">{x.c}</span>
              <ArrowRight size={13} className="text-neutral-600 group-hover:text-white group-hover:translate-x-0.5 transition" />
            </Link>
          ))}
        </div>
        <p className="text-center text-neutral-500 text-sm mt-5">Private chefs from ₹75,000/month · and other cities on request</p>
      </section>

      {/* Editorial closing */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG_PLATED} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 py-32 text-center">
          <div className="font-script text-3xl text-[#F5B041]">an invitation</div>
          <h2 className="font-serif-display text-5xl md:text-6xl leading-tight mt-4">
            Your kitchen deserves a name<br/>on the door.
          </h2>
          <div className="mt-10 flex items-center justify-center gap-4">
            <Link to="/chefs" className="btn-primary small-caps text-[11px] inline-flex items-center gap-2" data-testid="closing-cta">
              Begin <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
